import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../../context/AuthContext'
import ImageList from './ImageList'
import ImageDetail from './ImageDetail'
import ImageUpload from './ImageUpload'
import BulkTagModal from '../../../components/BulkTagModal'
import '../../../components/BulkTagModal.css'
import './ImageManager.css'

export default function ImageManager() {
  const { api } = useAuth()
  const [images, setImages] = useState([])
  const [selected, setSelected] = useState(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [continuationToken, setContinuationToken] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Tag filter
  const [allTags, setAllTags] = useState([])
  const [activeTag, setActiveTag] = useState('')
  const [tagFilterImageIds, setTagFilterImageIds] = useState(null)

  // Bulk selection
  const [checked, setChecked] = useState(new Set())
  const [showBulkTag, setShowBulkTag] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)

  useEffect(() => {
    api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
  }, [api])

  useEffect(() => {
    if (!activeTag) {
      setTagFilterImageIds(null)
      return
    }
    api(`/tags/filter?tag=${encodeURIComponent(activeTag)}&type=image`)
      .then(d => setTagFilterImageIds(d.image_ids || []))
      .catch(() => setTagFilterImageIds([]))
  }, [activeTag, api])

  const loadImages = useCallback(async (append = false) => {
    setLoading(true)
    if (!append) setStatus('Loading images...')

    try {
      const params = new URLSearchParams({
        per_page: '50',
        sort_order: sortOrder,
      })
      if (append && continuationToken) {
        params.set('continuation_token', continuationToken)
      }

      const data = await api(`/images?${params}`)

      const result = data.result || data
      const list = result.images || []
      const token = result.continuation_token || null

      if (append) {
        setImages(prev => [...prev, ...list])
      } else {
        setImages(list)
      }

      setContinuationToken(token)
      setStatus(`${append ? images.length + list.length : list.length} images loaded`)
    } catch (e) {
      console.error('Load images error:', e)
      setStatus(`Error: ${e.message}`)
      if (!append) setImages([])
    } finally {
      setLoading(false)
    }
  }, [api, sortOrder, continuationToken])

  useEffect(() => {
    loadImages(false)
  }, [sortOrder]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSelect(imageId) {
    try {
      setStatus('Loading image details...')
      const data = await api(`/images/${imageId}`)
      setSelected(data.result || data)
      setStatus('')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleDelete(imageId) {
    try {
      setStatus('Deleting...')
      await api(`/images/${imageId}`, { method: 'DELETE' })
      setSelected(null)
      setStatus('✅ Image deleted')
      loadImages(false)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function handleSearch(term) {
    setSearchTerm(term)
  }

  function handleSortChange(order) {
    setSortOrder(order)
    setContinuationToken(null)
  }

  function handleLoadMore() {
    if (continuationToken) {
      loadImages(true)
    }
  }

  function handleTagFilter(tag) {
    setActiveTag(tag)
  }

  async function handleUploadComplete(imageId) {
    setStatus('✅ Upload complete!')
    loadImages(false)
    if (imageId) {
      setTimeout(() => handleSelect(imageId), 1000)
    }
  }

  // Bulk selection helpers
  function handleToggle(imageId) {
    setChecked(prev => {
      const next = new Set(prev)
      next.has(imageId) ? next.delete(imageId) : next.add(imageId)
      return next
    })
  }

  function handleToggleAll(displayImages) {
    if (checked.size === displayImages.length && displayImages.every(img => checked.has(img.id))) {
      setChecked(new Set())
    } else {
      setChecked(new Set(displayImages.map(img => img.id)))
    }
  }

  function clearSelection() {
    setChecked(new Set())
  }

  function getCheckedItems() {
    return images
      .filter(img => checked.has(img.id))
      .map(img => ({ id: img.id, title: img.filename || img.id }))
  }

  // Bulk delete
  async function handleBulkDelete() {
    const ids = [...checked]
    if (!confirm(`Delete ${ids.length} images from Cloudflare Images?\n\nThis cannot be undone!`)) return

    setBulkBusy(true)
    setStatus(`Deleting ${ids.length} images...`)
    let success = 0
    let errors = 0

    for (const id of ids) {
      try {
        await api(`/images/${id}`, { method: 'DELETE' })
        success++
        setStatus(`Deleting... ${success}/${ids.length}`)
      } catch {
        errors++
      }
    }

    setStatus(`✅ ${success} deleted${errors ? `, ${errors} failed` : ''}`)
    setBulkBusy(false)
    clearSelection()
    setSelected(null)
    loadImages(false)
  }

  // Client-side search filter
  const filteredImages = searchTerm
    ? images.filter(img =>
        (img.filename || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (img.id || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : images

  return (
    <div className="image-manager">
      <div className="image-manager-header">
        <h1>Image Manager</h1>
        <span className="status-text">{status}</span>
      </div>

      <div className="image-manager-layout">
        <div className="image-manager-sidebar">
          <ImageList
            images={filteredImages}
            selected={selected}
            loading={loading}
            hasMore={!!continuationToken}
            onSelect={handleSelect}
            onSearch={handleSearch}
            onLoadMore={handleLoadMore}
            onSort={handleSortChange}
            sortOrder={sortOrder}
            allTags={allTags}
            activeTag={activeTag}
            onTagFilter={handleTagFilter}
            tagFilterImageIds={tagFilterImageIds}
            checked={checked}
            onToggle={handleToggle}
            onToggleAll={handleToggleAll}
            onClearSelection={clearSelection}
            onBulkTag={() => setShowBulkTag(true)}
            onBulkDelete={handleBulkDelete}
            bulkBusy={bulkBusy}
          />
        </div>

        <div className="image-manager-main">
          {selected ? (
            <ImageDetail
              image={selected}
              onDelete={handleDelete}
              setStatus={setStatus}
            />
          ) : (
            <div className="no-selection">
              <p>Select an image from the list to view details</p>
            </div>
          )}

          <ImageUpload onComplete={handleUploadComplete} setStatus={setStatus} />
        </div>
      </div>

      {showBulkTag && (
        <BulkTagModal
          items={getCheckedItems()}
          idKey="image_id"
          onClose={() => setShowBulkTag(false)}
          onComplete={() => {
            setShowBulkTag(false)
            setStatus('✅ Bulk tags applied')
            clearSelection()
            api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
          }}
        />
      )}
    </div>
  )
}
